import { useCallback, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import RenderReactPdfViewer from '@/components/react-pdf-viewer/render-react-pdf-viewer'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import ContentViewerLoadingSpinner from '@/content-viewer/components/common/ContentViewerLoadingStates'
import { MimeType } from '@/constants/constants-mime-types'
import { getConvertedPdfViewUrl } from '@/medical-billing-summaries/asset/fetch/get-converted-pdf-view-url'

type MedSummaryConvertedPdfRendererProps = {
  assetId: string
  userFileId: string
  title: string
  pdfHighlights: PaxtonHighlightArea[]
  startingPage?: number
}

function withPdfExtension(fileName: string) {
  return fileName.toLowerCase().endsWith('.pdf') ? fileName : `${fileName}.pdf`
}

/**
 * Renders a billing-summary source file's converted PDF (AI-94). Non-PDF sources are
 * extracted against a stored conversion, so citation highlight geometry only lines up
 * on this document. Resolves a short-lived signed URL and streams the bytes at render time.
 */
export default function MedSummaryConvertedPdfRenderer(props: MedSummaryConvertedPdfRendererProps) {
  const { assetId, userFileId, title, pdfHighlights, startingPage } = props

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [loadAttempt, setLoadAttempt] = useState(0)

  const retry = useCallback(() => setLoadAttempt((attempt) => attempt + 1), [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(false)

    const load = async () => {
      const capability = await getConvertedPdfViewUrl(assetId, userFileId)
      const response = await fetch(capability.viewable_url)
      if (!response.ok) {
        throw new Error(`Converted PDF fetch failed with status ${response.status}`)
      }
      const blob = await response.blob()
      return new File([blob], withPdfExtension(title || userFileId), { type: MimeType.PDF })
    }

    load()
      .then((file) => {
        if (cancelled) return
        setPdfFile(file)
      })
      .catch((loadError) => {
        if (cancelled) return
        Sentry.captureException(loadError, { extra: { assetId, userFileId, title } })
        setError(true)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [assetId, userFileId, title, loadAttempt])

  if (loading) {
    return <ContentViewerLoadingSpinner size="small" />
  }

  if (error || !pdfFile) {
    return <ContentViewerErrorState message="Failed to load PDF file." onRetry={retry} />
  }

  return <RenderReactPdfViewer pdfFile={pdfFile} highlightAreas={pdfHighlights} startingPage={startingPage} />
}
